/**
 * Utility functions for handling upload errors
 */
export interface UploadError {
	fileName: string;
	error: string;
}

export interface ErrorHandlerResult {
	errorMessage: string;
	errorDetails: string;
}

/**
 * Formats upload errors into user-friendly messages
 * @param uploadErrors Array of upload errors
 * @returns Formatted error message and details
 */
export const formatUploadErrors = (uploadErrors: UploadError[]): ErrorHandlerResult => {
	if (uploadErrors.length === 0) {
		return {
			errorMessage: '',
			errorDetails: ''
		};
	}

	const singleError = uploadErrors.length === 1 ? uploadErrors[0] : null;
	const errorMessage = singleError
		? `Failed to upload file "${singleError.fileName}"`
		: `Failed to upload ${uploadErrors.length} file(s)`;
		
	const errorDetails = uploadErrors.length === 1
		? singleError!.error // server message in details
		: uploadErrors.map(err => `• ${err.fileName}: ${err.error}`).join('\n');
	
	return {
		errorMessage,
		errorDetails
	};
};

/**
 * Handles upload errors by formatting them and calling the provided error handler
 * @param uploadErrors Array of upload errors
 * @param showError Function to display the error
 */
export const handleUploadErrors = (
	uploadErrors: UploadError[], 
	showError: (message: string, details?: string) => void
): void => {
	if (uploadErrors.length > 0) {
		const { errorMessage, errorDetails } = formatUploadErrors(uploadErrors);
		showError(errorMessage, errorDetails);
	}
};
